// Phase 9: Audit Trail. Reads the same append-only society_audit_logs
// collection every phase since Phase 1 has been writing to (see backend
// society_audit_logs / _society_audit_log) - this page adds filters
// (category/action/entity/date range/search) and a human-readable "View
// Details" drill-down over data that already existed, rather than a second
// logging system.
import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../../components/ui/dialog";
import { Download, Eye } from "lucide-react";
import { toast } from "sonner";
import { SectionCard } from "./shared";

const CATEGORIES = ["Masters", "Transactions", "Migration", "Billing Integration", "Reconciliation", "Accounting Controls", "Other"];

function DiffBlock({ label, value }) {
  const entries = Object.entries(value || {}).filter(([k]) => !["client_id", "id"].includes(k));
  if (!entries.length) return <div className="text-xs text-[#9CA3AF]">{label}: (none)</div>;
  return (
    <div>
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">{label}</div>
      <div className="space-y-0.5 rounded-sm border border-[#E5E7EB] bg-[#F9FAFB] p-2 text-xs">
        {entries.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3">
            <span className="text-[#6B7280]">{k}</span>
            <span className="max-w-[60%] truncate text-right font-medium text-[#111827]">{typeof v === "object" ? JSON.stringify(v) : String(v ?? "-")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AuditDetailDialog({ log, onOpenChange }) {
  return (
    <Dialog open={!!log} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Audit log details</DialogTitle></DialogHeader>
        {log && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-[#6B7280]">When: </span>{log.createdAt ? new Date(log.createdAt).toLocaleString() : "-"}</div>
              <div><span className="text-[#6B7280]">By: </span>{log.actorName || log.actorRole || "-"}</div>
              <div><span className="text-[#6B7280]">Action: </span>{log.action}</div>
              <div><span className="text-[#6B7280]">Category: </span>{log.category || "-"}</div>
              <div className="col-span-2"><span className="text-[#6B7280]">Entity: </span>{log.entityType} {log.entityId ? `#${log.entityId}` : ""}</div>
              {log.note && <div className="col-span-2"><span className="text-[#6B7280]">Note: </span>{log.note}</div>}
            </div>
            {(log.before !== undefined || log.after !== undefined) && (
              <div className="grid gap-3 sm:grid-cols-2">
                <DiffBlock label="Before" value={log.before} />
                <DiffBlock label="After" value={log.after} />
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AuditTrailPage() {
  const [auditLogs, setAuditLogs] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [action, setAction] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [q, setQ] = useState("");
  // PHASE 19 item 4: "User" and "Voucher/Record No." are the two named
  // filters the spec adds on top of Date/Module(Category)/Action/Search,
  // which already existed from Phase 9.
  const [actor, setActor] = useState("");
  const [recordNo, setRecordNo] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (category) params.category = category;
      if (action) params.action = action;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (q) params.q = q;
      if (actor) params.actor = actor;
      if (recordNo) params.recordNo = recordNo;
      const [logsRes, attachmentsRes] = await Promise.all([
        api.get("/society/audit/logs", { params }).catch(() => ({ data: [] })),
        api.get("/society/attachments").catch(() => ({ data: [] })),
      ]);
      setAuditLogs(Array.isArray(logsRes.data) ? logsRes.data : []);
      setAttachments(Array.isArray(attachmentsRes.data) ? attachmentsRes.data : []);
    } catch (err) {
      toast.error(formatErr(err, "Could not load audit trail"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [category, action, fromDate, toDate, actor, recordNo]);

  const filtered = useMemo(() => {
    if (!q) return auditLogs;
    const needle = q.toLowerCase();
    return auditLogs.filter((l) => `${l.action} ${l.entityType} ${l.actorName} ${l.note}`.toLowerCase().includes(needle));
  }, [auditLogs, q]);

  const actions = useMemo(() => Array.from(new Set(auditLogs.map((l) => l.action))).sort(), [auditLogs]);
  const actors = useMemo(() => Array.from(new Set(auditLogs.map((l) => l.actorName).filter(Boolean))).sort(), [auditLogs]);

  const exportLogs = async () => {
    try {
      const res = await api.get("/society/audit/export", { params: { report: "audit_logs" }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "society-audit_logs.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatErr(err, "Could not export audit logs"));
    }
  };

  const openAttachment = async (attachment) => {
    try {
      const res = await api.get(`/society/attachments/${attachment.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(formatErr(err, "Could not open attachment"));
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Audit Trail</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Immutable, append-only record of Masters, Transactions, Migration, Billing Integration and Reconciliation actions, plus every proof document uploaded against vouchers, bills and payments.</p>
      </div>

      <SectionCard title="Audit log" subtitle="Every entry here is permanent - nothing is ever edited or removed" actions={<Button variant="outline" size="sm" onClick={exportLogs}><Download className="mr-2 h-4 w-4" /> Export audit logs</Button>}>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div className="w-44 space-y-1">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Category</div>
            <Select value={category || "__all__"} onValueChange={(v) => setCategory(v === "__all__" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All categories</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-40 space-y-1">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Action</div>
            <Select value={action || "__all__"} onValueChange={(v) => setAction(v === "__all__" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All actions</SelectItem>
                {actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-36 space-y-1">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">From</div>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div className="w-36 space-y-1">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">To</div>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <div className="w-44 space-y-1">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">User</div>
            <Select value={actor || "__all__"} onValueChange={(v) => setActor(v === "__all__" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All users</SelectItem>
                {actors.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-44 space-y-1">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Voucher / Record No.</div>
            <Input value={recordNo} onChange={(e) => setRecordNo(e.target.value)} placeholder="e.g. PMT/2026-27/00007" />
          </div>
          <div className="w-56 space-y-1">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Search</div>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Actor, entity, note..." />
          </div>
        </div>
        <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
          <Table>
            <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>Category</TableHead><TableHead>User</TableHead><TableHead>Action</TableHead><TableHead>Entity</TableHead><TableHead>Note</TableHead><TableHead className="text-right">Details</TableHead></TableRow></TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="py-8 text-center text-[#6B7280]">Loading...</TableCell></TableRow>
              ) : filtered.slice(0, 200).map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="text-xs">{log.createdAt ? new Date(log.createdAt).toLocaleString() : "-"}</TableCell>
                  <TableCell className="text-xs">{log.category || "-"}</TableCell>
                  <TableCell>{log.actorName || log.actorRole || "-"}</TableCell>
                  <TableCell>{log.action}</TableCell>
                  <TableCell>{log.entityType} {log.entityId ? `#${String(log.entityId).slice(0, 8)}` : ""}</TableCell>
                  <TableCell className="max-w-[220px] truncate text-xs text-[#6B7280]">{log.note || "-"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" onClick={() => setSelected(log)}><Eye className="mr-1 h-3.5 w-3.5" /> View</Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={7} className="py-8 text-center text-[#6B7280]">No audit log entries match these filters.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </SectionCard>

      <SectionCard title="Attachments and proof documents">
        <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
          <Table>
            <TableHeader><TableRow><TableHead>Uploaded</TableHead><TableHead>Type</TableHead><TableHead>Label</TableHead><TableHead>File</TableHead><TableHead>By</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-[#6B7280]">Loading...</TableCell></TableRow>
              ) : attachments.slice(0, 30).map((attachment) => (
                <TableRow key={attachment.id}>
                  <TableCell className="text-xs">{attachment.createdAt ? new Date(attachment.createdAt).toLocaleString() : "-"}</TableCell>
                  <TableCell>{attachment.entityType}</TableCell>
                  <TableCell>{attachment.label || "-"}</TableCell>
                  <TableCell className="max-w-[260px] truncate">{attachment.filename || "-"}</TableCell>
                  <TableCell>{attachment.createdBy || attachment.createdByRole || "-"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" onClick={() => openAttachment(attachment)}><Download className="mr-2 h-4 w-4" /> Open</Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && attachments.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-[#6B7280]">No attachments uploaded yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </SectionCard>

      <AuditDetailDialog log={selected} onOpenChange={(open) => !open && setSelected(null)} />
    </div>
  );
}
